import { useEffect, useMemo, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import api from "@/lib/api";
import Layout from "@/components/Layout";
import { useAuth } from "@/lib/auth";
import { ChevronRight, Loader2, AlertTriangle, RefreshCw, ExternalLink, Plus, Copy, X, Lock, Bookmark, BookmarkCheck } from "lucide-react";
import { toast } from "sonner";
import AddCustomJobModal from "@/components/AddCustomJobModal";

export default function Dashboard() {
  const [data, setData] = useState(null);
  const [refreshing, setRefreshing] = useState(false);
  const [location, setLocation] = useState("all");
  const navigate = useNavigate();
  const { user } = useAuth();
  const isPaid = !!(user?.is_paid);
  const isAdmin = !!(user?.is_admin);
  const [upgradeOpen, setUpgradeOpen] = useState(false);
  const [showBookmarkedOnly, setShowBookmarkedOnly] = useState(false);

  const visibleJobs = useMemo(
    () => sortJobs(data?.jobs || []).filter((e) => !showBookmarkedOnly || e.bookmarked || e.user_job?.bookmarked),
    [data?.jobs, showBookmarkedOnly]
  );

  const toggleBookmark = async (job_id) => {
    // Optimistic
    setData((cur) => cur ? {
      ...cur,
      jobs: cur.jobs.map((e) => e.job.id === job_id ? { ...e, bookmarked: !e.bookmarked, user_job: e.user_job ? { ...e.user_job, bookmarked: !e.bookmarked } : e.user_job } : e),
    } : cur);
    try {
      await api.post(`/jobs/${job_id}/bookmark`);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Couldn't save bookmark.");
      // Rollback
      setData((cur) => cur ? {
        ...cur,
        jobs: cur.jobs.map((e) => e.job.id === job_id ? { ...e, bookmarked: !e.bookmarked, user_job: e.user_job ? { ...e.user_job, bookmarked: !e.bookmarked } : e.user_job } : e),
      } : cur);
    }
  };

  const [feedError, setFeedError] = useState(null);
  const [addJobOpen, setAddJobOpen] = useState(false);

  const load = async (loc = location) => {
    try {
      const res = await api.get(`/jobs/feed${loc && loc !== "all" ? `?location=${encodeURIComponent(loc)}` : ""}`);
      setData(res.data);
      setFeedError(null);
      if (!res.data.complete) {
        toast.warning("Profile incomplete. Finish onboarding to unlock your feed.");
      }
    } catch (err) {
      setFeedError(err?.response?.data?.detail || "We couldn't reach the job feed. Check your connection and try again.");
      toast.error("Job feed failed to load.");
    }
  };

  useEffect(() => { load("all"); }, []); // eslint-disable-line

  // Poll while any jobs are still generating
  useEffect(() => {
    if (!data?.complete) return;
    const stillGenerating = data.jobs.some(
      (e) => !e.user_job || e.user_job?.materials_status === "generating"
    );
    if (!stillGenerating) return;
    const t = setTimeout(() => load(location), 5000);
    return () => clearTimeout(t);
  }, [data, location]); // eslint-disable-line

  const refresh = async () => {
    setRefreshing(true);
    try {
      await api.post("/jobs/refresh");
      await load(location);
      toast.success("Feed refreshed");
    } finally { setRefreshing(false); }
  };

  const onLocationChange = (e) => {
    const v = e.target.value;
    setLocation(v);
    load(v);
  };

  const dismissJob = async (job) => {
    // Optimistic remove + 5-minute undo toast.
    const snapshot = data;
    setData({ ...data, jobs: data.jobs.filter((e) => e.job.id !== job.id) });
    try {
      await api.post(`/jobs/${job.id}/dismiss`);
      toast(`Removed "${job.title}" from feed.`, {
        duration: 300000, // 5 minutes
        action: {
          label: "Undo",
          onClick: async () => {
            try {
              await api.post(`/jobs/${job.id}/undismiss`);
              setData(snapshot);
              toast.success("Restored to feed.");
            } catch {
              toast.error("Couldn't undo. Try refreshing.");
            }
          },
        },
      });
    } catch {
      setData(snapshot);
      toast.error("Couldn't dismiss job.");
    }
  };

  if (!data) {
    if (feedError) {
      return (
        <Layout>
          <div className="max-w-2xl mx-auto px-5 md:px-12 py-12 sm:py-20" data-testid="feed-error-state">
            <div className="panel p-6 sm:p-10 text-center corner-marks relative">
              <AlertTriangle className="w-10 h-10 text-destructive mx-auto mb-6" strokeWidth={1.5} />
              <div className="terminal-label text-destructive mb-3">[ FEED.ERROR ]</div>
              <h1 className="font-display text-2xl uppercase font-bold mb-3">Couldn&apos;t load the feed</h1>
              <p className="text-sm text-muted-foreground mb-6">{feedError}</p>
              <button data-testid="feed-error-retry" onClick={() => load("all")} className="btn-primary inline-flex items-center gap-2">
                <RefreshCw className="w-3 h-3" /> Try again
              </button>
            </div>
          </div>
        </Layout>
      );
    }
    return <Layout><div className="p-12 text-center text-muted-foreground text-xs uppercase tracking-[0.3em]">LOADING FEED<span className="blink-caret"/></div></Layout>;
  }

  if (!data.complete) {
    return (
      <Layout>
        <div className="max-w-3xl mx-auto px-5 md:px-12 py-12 sm:py-20">
          <div className="panel p-6 sm:p-10 text-center corner-marks relative">
            <AlertTriangle className="w-10 h-10 text-accent-amber mx-auto mb-6" strokeWidth={1.5} />
            <div className="terminal-label text-accent-amber mb-4">[ PROFILE.INCOMPLETE ]</div>
            <h1 className="font-display text-2xl uppercase font-bold mb-3">Finish your profile first</h1>
            <p className="text-sm text-muted-foreground mb-2">Sisyphean needs a complete profile before generating tailored materials.</p>
            <p className="text-xs text-muted-foreground mb-8">Missing: <span className="text-accent-amber font-mono">{data.gaps.join(", ")}</span></p>
            <Link to="/onboarding" data-testid="finish-onboarding-link" className="btn-primary">Continue onboarding →</Link>
          </div>
        </div>
      </Layout>
    );
  }

  const ready = data.jobs.filter((x) => x.user_job?.materials_status === "ready");
  const generating = data.jobs.filter((x) => !x.user_job || x.user_job?.materials_status === "generating");

  return (
    <Layout>
      <div className="max-w-[1600px] mx-auto px-5 md:px-12 py-6 sm:py-10">
        {/* Free-trial banner: only for active-trial users who are NOT paid and NOT admin */}
        {user?.has_active_access && !isPaid && !isAdmin && (
          <div className="panel p-5 mb-6 border-foreground/30" data-testid="free-trial-banner">
            <div className="flex items-start justify-between gap-4 flex-wrap">
              <div className="min-w-0">
                <div className="terminal-label text-primary mb-1">[ FREE.TRIAL ]</div>
                <p className="text-sm text-foreground/85 leading-relaxed max-w-xl">
                  Free trial: tailor up to {data?.trial_quota ?? 3} applications total{typeof data?.trial_used === "number" ? ` (${data.trial_used} of ${data.trial_quota} used)` : ""}. Upgrade any time to open the rest of the feed and add custom jobs.
                </p>
              </div>
              <Link to="/subscription" data-testid="free-trial-cta" className="btn-primary text-[10px] inline-flex items-center gap-2 shrink-0">
                See plans →
              </Link>
            </div>
          </div>
        )}
        {!user?.has_active_access && (
          <div className="panel p-5 mb-6 border-primary/40" data-testid="trial-expired-banner" style={{ borderColor: "hsl(var(--primary) / 0.4)" }}>
            <div className="flex items-start justify-between gap-4 flex-wrap">
              <div>
                <div className="terminal-label text-primary mb-1">
                  {user?.previously_deleted ? "[ WELCOME.BACK ]" : "[ TRIAL.EXPIRED ]"}
                </div>
                <p className="text-sm text-foreground/85 leading-relaxed max-w-xl">
                  {user?.previously_deleted
                    ? "Glad to have you back. Since you've used Sisyphean before, the free trial doesn't apply. Choose a plan to start sending applications again."
                    : `You've used all ${data?.trial_quota ?? 3} of your free tailored applications. You can still browse the feed and review what you've already generated, but new tailored materials and sending applications are paused until you upgrade.`}
                </p>
              </div>
              <Link to="/subscription" data-testid="trial-expired-cta" className="btn-primary text-[10px] inline-flex items-center gap-2 shrink-0">
                {user?.previously_deleted ? "Choose a plan →" : "Upgrade →"}
              </Link>
            </div>
          </div>
        )}
        <div className="flex items-end justify-between flex-wrap gap-4 mb-6 sm:mb-10">
          <div>
            <div className="terminal-label text-primary">[ DAILY.FEED / {new Date().toLocaleDateString("en-US", { day: "2-digit", month: "short", year: "numeric" }).toUpperCase()} ]</div>
            <h1 className="font-display text-2xl sm:text-4xl md:text-5xl uppercase font-bold tracking-tight mt-2">Today&apos;s matches</h1>
            <p className="text-sm text-muted-foreground mt-2 max-w-xl">
              {ready.length} ready · {generating.length} generating in background. Highest combined score first.
            </p>
          </div>
          <div className="flex items-center gap-2 w-full sm:w-auto">
            <button
              data-testid="add-custom-job-btn"
              onClick={() => { if (isPaid) { setAddJobOpen(true); } else { setUpgradeOpen(true); } }}
              className={`btn-primary inline-flex items-center justify-center gap-2 flex-1 sm:flex-none ${isPaid ? "" : "opacity-50"}`}
              title={isPaid ? "Add a custom job listing" : "Upgrade to add custom jobs"}
            >
              {isPaid ? <Plus className="w-3 h-3" /> : <Lock className="w-3 h-3" />}
              {isPaid ? "Add a job" : "Add a job"}
            </button>
            <button data-testid="refresh-feed" onClick={refresh} disabled={refreshing} className="btn-secondary inline-flex items-center justify-center gap-2 flex-1 sm:flex-none">
              <RefreshCw className={`w-3 h-3 ${refreshing ? "animate-spin" : ""}`} /> Refresh
            </button>
          </div>
        </div>

        {/* Location filter */}
        <div className="panel p-4 mb-px flex items-center gap-3 flex-wrap" data-testid="location-filter-panel">
          <span className="terminal-label">Filter by location:</span>
          <select
            data-testid="location-filter"
            value={location}
            onChange={onLocationChange}
            className="input-base w-auto min-w-[200px] py-2"
          >
            <option value="all">All locations</option>
            {(data.locations || []).map((loc) => (
              <option key={loc} value={loc}>{loc}</option>
            ))}
          </select>
          {location !== "all" && (
            <button onClick={() => { setLocation("all"); load("all"); }} className="btn-ghost" data-testid="location-clear">
              Clear ✕
            </button>
          )}
          <button
            onClick={() => setShowBookmarkedOnly((v) => !v)}
            className={`text-[10px] tracking-[0.2em] uppercase font-mono px-2.5 py-1 border transition-colors inline-flex items-center gap-1.5 ${showBookmarkedOnly ? "border-primary text-primary bg-primary/5" : "border-foreground/20 text-muted-foreground hover:border-foreground/50 hover:text-foreground"}`}
            data-testid="bookmark-filter-toggle"
            title={showBookmarkedOnly ? "Show all" : "Show bookmarked only"}
          >
            {showBookmarkedOnly ? <BookmarkCheck className="w-3 h-3" /> : <Bookmark className="w-3 h-3" />}
            Bookmarked
          </button>
          <span className="terminal-label ml-auto">{data.jobs.length} listing{data.jobs.length === 1 ? "" : "s"}</span>
        </div>

        <div className="border border-foreground/30">
          {/* Header row */}
          <div className="hidden md:grid grid-cols-12 gap-px bg-foreground/30 border-b border-foreground/30">
            <div className="col-span-1 bg-background p-3 terminal-label">#</div>
            <div className="col-span-4 bg-background p-3 terminal-label">Role / Company</div>
            <div className="col-span-2 bg-background p-3 terminal-label">Location</div>
            <div className="col-span-2 bg-background p-3 terminal-label">Match</div>
            <div className="col-span-2 bg-background p-3 terminal-label">Enjoyment</div>
            <div className="col-span-1 bg-background p-3 terminal-label text-right">→</div>
          </div>
          {/* Rows - user_custom + test_fixture jobs pinned to top, then by sort score */}
          {visibleJobs.map((entry, idx) => {
            const isOnTrial = !isPaid;
            const explicitlyUnlocked = entry.user_job?.trial_unlocked === true;
            const locked = isOnTrial && !explicitlyUnlocked;
            const isBookmarked = !!(entry.bookmarked || entry.user_job?.bookmarked);
            return (
              <FeedRow
                key={entry.job.id}
                idx={idx}
                entry={entry}
                locked={locked}
                bookmarked={isBookmarked}
                onToggleBookmark={() => toggleBookmark(entry.job.id)}
                onClick={() => {
                  if (locked) { setUpgradeOpen(true); }
                  else { navigate(`/applications/${entry.job.id}`); }
                }}
                onDismiss={() => dismissJob(entry.job)}
              />
            );
          })}
          {data.jobs.length === 0 && (
            <div className="p-12 text-center" data-testid="dashboard-empty-state">
              <Loader2 className="w-6 h-6 text-muted-foreground mx-auto mb-4 animate-spin" strokeWidth={1.5} />
              <div className="terminal-label mb-2">[ FETCHING.JOBS ]</div>
              <p className="text-sm text-muted-foreground max-w-md mx-auto leading-relaxed">
                We&apos;re pulling fresh listings for you. This usually takes 10-30 seconds the first time. Check back shortly or hit Refresh.
              </p>
            </div>
          )}
        </div>
      </div>
      <AddCustomJobModal
        open={addJobOpen}
        onClose={() => setAddJobOpen(false)}
        onCreated={() => load(location)}
      />
      {upgradeOpen && <UpgradeModal onClose={() => setUpgradeOpen(false)} />}
    </Layout>
  );
}

function UpgradeModal({ onClose }) {
  useEffect(() => {
    const onKey = (e) => { if (e.key === "Escape") onClose(); };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [onClose]);
  return (
    <div
      data-testid="upgrade-modal"
      className="fixed inset-0 z-[140] flex items-center justify-center px-4 bg-background/85 backdrop-blur-sm"
      onClick={(e) => { if (e.target === e.currentTarget) onClose(); }}
    >
      <div className="panel-glass corner-marks relative w-full max-w-md p-6 sm:p-8">
        <button onClick={onClose} aria-label="Close" className="absolute top-3 right-3 p-2 text-muted-foreground hover:text-foreground">
          <X className="w-4 h-4" />
        </button>
        <div className="terminal-label text-primary mb-3">[ UPGRADE.REQUIRED ]</div>
        <h3 className="font-display text-xl sm:text-2xl uppercase font-bold tracking-tight mb-3">
          Open the rest of the feed.
        </h3>
        <p className="text-sm text-foreground/80 leading-relaxed mb-2">
          Your free trial covers 3 tailored applications total. Upgrade to open every listing on the feed, add custom jobs, and send full applications.
        </p>
        <ul className="text-[12px] text-foreground/70 space-y-1.5 mb-6 mt-4">
          <li className="flex items-start gap-2"><span className="text-primary mt-0.5">▸</span> $200 lifetime for the first 100 founders</li>
          <li className="flex items-start gap-2"><span className="text-foreground/40 mt-0.5">▸</span> $30/month standard after founders close</li>
        </ul>
        <div className="flex justify-end gap-3">
          <button onClick={onClose} className="btn-ghost text-[11px]" data-testid="upgrade-modal-cancel">Not yet</button>
          <Link to="/subscription" onClick={onClose} className="btn-primary inline-flex items-center gap-2 text-[11px]" data-testid="upgrade-modal-cta">
            See plans <ChevronRight className="w-3 h-3" />
          </Link>
        </div>
      </div>
    </div>
  );
}

function sortJobs(jobs) {
  // Pin user-added (user_custom) and test fixtures to the top in their own buckets,
  // preserving the original ordering (which is already sorted by score).
  const isPinned = (e) => e.job.source === "user_custom" || e.job.source === "test_fixture";
  const pinned = jobs.filter(isPinned);
  const rest = jobs.filter((e) => !isPinned(e));
  return [...pinned, ...rest];
}

function ScoreBar({ value, color }) {
  return (
    <div className="flex items-center gap-2">
      <div className="flex-1 h-1 bg-surface relative">
        <div className="absolute left-0 top-0 h-full" style={{ width: `${value}%`, backgroundColor: color }} />
      </div>
      <span className="font-mono text-sm tabular-nums w-8 text-right" style={{ color }}>{value}</span>
    </div>
  );
}

function FeedRow({ entry, idx, onClick, onDismiss, onToggleBookmark, locked = false, bookmarked = false }) {
  const { job, user_job } = entry;
  const ready = user_job?.materials_status === "ready";
  const scored = ready || user_job?.materials_status === "locked" || (user_job && typeof user_job.match_score === "number" && user_job.match_score > 0);
  const matchColor = "#00FF41";
  const enjoyColor = "#FFB000";
  const isCustom = job.source === "user_custom";
  const isTestFixture = job.source === "test_fixture";
  const isPinned = isCustom || isTestFixture;

  const pinTint = isPinned ? { backgroundColor: "hsl(var(--primary) / 0.07)" } : undefined;
  const pinHoverClass = isPinned ? "hover:!bg-[hsl(var(--primary)/0.12)]" : "hover:bg-surface-hover";

  const copyListing = async (e) => {
    e.stopPropagation();
    const lines = [
      job.title,
      job.company ? `${job.company}` : null,
      job.location ? `${job.location}${job.remote ? " · Remote" : ""}` : null,
      job.salary ? `Salary: ${job.salary}` : null,
      job.tags?.length ? `Tags: ${job.tags.join(", ")}` : null,
      "",
      job.description || "",
    ].filter(Boolean).join("\n");
    try {
      await navigator.clipboard.writeText(lines);
      toast.success("Full listing copied to clipboard.");
    } catch {
      toast.error("Couldn't copy to clipboard.");
    }
  };

  return (
    <div
      data-testid={`job-row-${idx}${locked ? "-locked" : ""}`}
      onClick={onClick}
      style={pinTint}
      className={`md:grid md:grid-cols-12 gap-2 md:gap-px md:bg-border border-b border-border last:border-b-0 cursor-pointer transition-colors group p-4 md:p-0 relative ${
        isPinned ? pinHoverClass : "bg-background hover:bg-surface-hover"
      } ${locked ? "opacity-40 hover:opacity-60" : ""}`}
      title={locked ? "Upgrade to unlock this match" : undefined}
    >
      {locked && (
        <div className="absolute top-2 right-2 md:top-1/2 md:-translate-y-1/2 md:right-12 inline-flex items-center gap-1 px-1.5 py-0.5 border border-foreground/30 bg-background/70 text-[9px] uppercase tracking-[0.2em] font-mono text-foreground/70 z-10 pointer-events-none">
          <Lock className="w-2.5 h-2.5" /> Locked
        </div>
      )}
      {/* MOBILE card layout (hidden on md+) */}
      <div className="md:hidden">
        <div className="flex items-center justify-between gap-2 mb-2">
          <div className="flex items-center gap-2 flex-wrap min-w-0">
            <span className={`font-mono text-xs tabular-nums ${isPinned ? "text-primary" : "text-muted-foreground"}`}>
              {isPinned ? "★" : String(idx + 1).padStart(3, "0")}
            </span>
            {isCustom && <span data-testid={`job-row-${idx}-custom-chip`} className="text-[9px] uppercase tracking-[0.22em] font-mono px-1.5 py-0.5 border border-primary/40 text-primary">CUSTOM</span>}
            {isTestFixture && <span data-testid={`job-row-${idx}-test-chip`} className="text-[9px] uppercase tracking-[0.22em] font-mono px-1.5 py-0.5 border border-accent-amber/60 text-accent-amber" style={{ backgroundColor: "hsl(var(--accent-amber) / 0.08)" }}>TEST</span>}
          </div>
          <div className="flex items-center gap-2 flex-shrink-0">
            {!ready && user_job?.materials_status === "generating" && <Loader2 className="w-3 h-3 animate-spin text-muted-foreground" />}
            {!user_job && <span className="text-[10px] uppercase tracking-[0.2em] text-muted-foreground">QUEUED</span>}
            {job.url && (
              <a href={job.url} target="_blank" rel="noopener noreferrer" onClick={(e) => e.stopPropagation()} className="text-muted-foreground hover:text-primary p-1">
                <ExternalLink className="w-3 h-3" />
              </a>
            )}
            <button
              onClick={(e) => { e.stopPropagation(); onToggleBookmark?.(); }}
              title={bookmarked ? "Remove bookmark" : "Bookmark for later"}
              className={`p-1 transition-colors ${bookmarked ? "text-primary" : "text-muted-foreground hover:text-primary"}`}
              data-testid={`job-row-${idx}-bookmark-mobile`}
            >
              {bookmarked ? <BookmarkCheck className="w-3.5 h-3.5" /> : <Bookmark className="w-3.5 h-3.5" />}
            </button>
            <button
              onClick={(e) => { e.stopPropagation(); onDismiss?.(); }}
              title="Not interested"
              className="text-muted-foreground hover:text-destructive p-1"
            >
              <X className="w-3 h-3" />
            </button>
            <ChevronRight className="w-4 h-4 text-muted-foreground group-hover:text-primary transition-colors" />
          </div>
        </div>
        <div className="text-base font-display font-medium uppercase tracking-tight break-words">{job.title}</div>
        <div className="text-xs text-muted-foreground mt-0.5 truncate">{job.company} · {job.location || "Remote"}</div>
        {scored && (
          <div className="mt-3 grid grid-cols-2 gap-3">
            <div>
              <div className="text-[9px] uppercase tracking-[0.2em] text-muted-foreground mb-1 font-mono">Match</div>
              <ScoreBar value={user_job.match_score} color={matchColor} />
            </div>
            <div>
              <div className="text-[9px] uppercase tracking-[0.2em] text-muted-foreground mb-1 font-mono">Enjoyment</div>
              <ScoreBar value={user_job.preference_score} color={enjoyColor} />
            </div>
          </div>
        )}
      </div>

      {/* DESKTOP grid row (hidden on mobile) */}
      <div style={pinTint} className={`hidden md:block md:col-span-1 md:p-4 font-mono text-xs tabular-nums ${isPinned ? "text-primary" : "md:bg-background text-muted-foreground"}`}>
        {isPinned ? "★" : String(idx + 1).padStart(3, "0")}
      </div>
      <div style={pinTint} className={`hidden md:block md:col-span-4 md:p-4 min-w-0 ${isPinned ? "" : "md:bg-background"}`}>
        <div className="flex items-center gap-2 flex-wrap">
          <div className="text-base font-display font-medium uppercase tracking-tight truncate">{job.title}</div>
          {isCustom && (
            <span className="shrink-0 text-[9px] uppercase tracking-[0.22em] font-mono px-1.5 py-0.5 border border-primary/40 text-primary" title="You added this listing">
              CUSTOM
            </span>
          )}
          {isTestFixture && (
            <span className="shrink-0 text-[9px] uppercase tracking-[0.22em] font-mono px-1.5 py-0.5 border border-accent-amber/60 text-accent-amber" style={{ backgroundColor: "hsl(var(--accent-amber) / 0.08)" }} title="LLM-generated test job (admin only)">
              TEST
            </span>
          )}
        </div>
        <div className="text-xs text-muted-foreground truncate mt-1">{job.company}</div>
      </div>
      <div style={pinTint} className={`hidden md:block md:col-span-2 md:p-4 text-xs text-muted-foreground truncate ${isPinned ? "" : "md:bg-background"}`}>
        {job.location || "Remote"}
      </div>
      <div style={pinTint} className={`hidden md:block md:col-span-2 md:p-4 ${isPinned ? "" : "md:bg-background"}`}>
        {scored ? <ScoreBar value={user_job.match_score} color={matchColor} /> : <span className="text-xs text-muted-foreground"> - </span>}
      </div>
      <div style={pinTint} className={`hidden md:block md:col-span-2 md:p-4 ${isPinned ? "" : "md:bg-background"}`}>
        {scored ? <ScoreBar value={user_job.preference_score} color={enjoyColor} /> : <span className="text-xs text-muted-foreground"> - </span>}
      </div>
      <div style={pinTint} className={`hidden md:flex md:col-span-1 md:p-4 items-center justify-end gap-2 ${isPinned ? "" : "md:bg-background"}`}>
        {!ready && user_job?.materials_status === "generating" && <Loader2 className="w-3 h-3 animate-spin text-muted-foreground" />}
        {!user_job && <span className="text-[10px] uppercase tracking-[0.2em] text-muted-foreground">QUEUED</span>}
        {isTestFixture && (
          <button data-testid={`job-row-${idx}-copy-listing`} onClick={copyListing} title="Copy full listing to clipboard (for pasting into an ATS)" className="text-muted-foreground hover:text-primary transition-colors p-1">
            <Copy className="w-3 h-3" />
          </button>
        )}
        {job.url && (
          <a data-testid={`job-row-${idx}-posting-link`} href={job.url} target="_blank" rel="noopener noreferrer" onClick={(e) => e.stopPropagation()} title="View original posting" className="text-muted-foreground hover:text-primary transition-colors">
            <ExternalLink className="w-3 h-3" />
          </a>
        )}
        <button
          data-testid={`job-row-${idx}-bookmark`}
          onClick={(e) => { e.stopPropagation(); onToggleBookmark?.(); }}
          title={bookmarked ? "Remove bookmark" : "Bookmark for later"}
          className={`p-1 transition-colors ${bookmarked ? "text-primary" : "text-muted-foreground hover:text-primary"}`}
        >
          {bookmarked ? <BookmarkCheck className="w-3.5 h-3.5" /> : <Bookmark className="w-3.5 h-3.5" />}
        </button>
        <button
          data-testid={`job-row-${idx}-dismiss`}
          onClick={(e) => { e.stopPropagation(); onDismiss?.(); }}
          title="Not interested - remove from feed"
          className="text-muted-foreground hover:text-destructive transition-colors p-1"
        >
          <X className="w-3 h-3" />
        </button>
        <ChevronRight className="w-4 h-4 text-muted-foreground group-hover:text-primary transition-colors" />
      </div>
    </div>
  );
}
